import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLockBodyScroll } from '../utils/useLockBodyScroll';
import { Icon } from './Icon';
import './Modal.css';

// Glass dialog; a bottom sheet on phones. Portalled to <body> so no ancestor can trap its fixed overlay.
// `label` names the dialog for assistive tech when the content draws its own heading instead of `title`.
// `mobile`: 'sheet' (default) slides up from the bottom; 'card' is centred on the screen — use it for anything with
// text fields: on iOS a bottom sheet ends up under the keyboard and in Safari's dead tap zone. A card stays centred
// in the visible part of the screen while the keyboard is up.
export function Modal({ isOpen, onClose, title, subtitle, label, size = 'md', mobile = 'sheet', dismissible = true, className = '', children }) {
    useLockBodyScroll(isOpen);
    const [viewport, setViewport] = useState(null);

    // iOS keeps the layout viewport full height under the keyboard and pans the visual viewport instead.
    // A card pinned to the layout viewport therefore drifts away from the field being typed in; pin the
    // overlay to the visual viewport (what is actually on screen) and centre the card inside that.
    useEffect(() => {
        const vv = window.visualViewport;
        if (!isOpen || mobile !== 'card' || !vv) return undefined;
        const update = () => setViewport({ top: Math.round(vv.offsetTop), height: Math.round(vv.height) });
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); setViewport(null); };
    }, [isOpen, mobile]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className={`modalOverlay${mobile === 'card' ? ' modalOverlay--card' : ''}`}
            style={viewport ? { '--vv-top': `${viewport.top}px`, '--vv-height': `${viewport.height}px` } : undefined}
            onMouseDown={(e) => { if (dismissible && e.target === e.currentTarget) onClose?.(); }}>
            <div className={`modal modal--${size} glass ${className}`} role="dialog" aria-modal="true" aria-label={title || label}>
                <span className="modalHandle" aria-hidden="true" />
                {dismissible && (
                    <button type="button" className="iconBtn iconBtn--sm modalClose" onClick={onClose} aria-label="Close">
                        <Icon name="close" size={16} />
                    </button>
                )}
                {(title || subtitle) && (
                    <div className="modalHead">
                        {title && <h2 className="modalTitle">{title}</h2>}
                        {subtitle && <p className="modalSub">{subtitle}</p>}
                    </div>
                )}
                <div className="modalBody">{children}</div>
            </div>
        </div>,
        document.body
    );
}
